import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/auth';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import type { PageTourKey } from '@/lib/tours/registry';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { usePageTourReady } from '@/store/pageTourReady';

interface UseAutoPageTourOptions {
  afterAnimationMs?: number;
  /** Registry tours use `promptAutoPageTour`; pass a custom launcher for multi-tab or dynamic-step tours. */
  onStart?: (tourKey: PageTourKey) => void;
  enabled?: boolean;
}

/**
 * Marks a page tour ready and auto-starts it on first visit when anchors are available.
 * Completion is persisted via the tour key in user preferences.
 */
export function useAutoPageTour(
  tourKey: PageTourKey | null | undefined,
  isReady: boolean,
  options?: UseAutoPageTourOptions,
) {
  const { promptAutoPageTour } = usePageTourActions();
  const { user } = useAuthStore();
  const scheduleRef = useRef<(() => void) | null>(null);
  const autoPromptedRef = useRef(false);
  const enabled = options?.enabled ?? true;
  const onStart = options?.onStart;
  const afterAnimationMs = options?.afterAnimationMs;

  usePageTourReady(tourKey ?? undefined, Boolean(tourKey) && isReady && enabled);

  useEffect(() => {
    if (!tourKey || !isReady || !enabled) return undefined;
    if (autoPromptedRef.current) return undefined;
    if (isPageTourComplete(user?.preferences, tourKey)) return undefined;

    scheduleRef.current?.();
    const launch = (key: PageTourKey) => {
      autoPromptedRef.current = true;
      if (onStart) {
        promptAutoPageTour(key, () => onStart(key));
        return;
      }
      promptAutoPageTour(key);
    };
    scheduleRef.current = scheduleAutoPageTour(launch, tourKey, user?.preferences, {
      // Page tours should start immediately; drawer tours pass an explicit delay.
      afterAnimationMs: afterAnimationMs ?? 0,
    });

    return () => {
      scheduleRef.current?.();
      scheduleRef.current = null;
    };
  }, [enabled, tourKey, isReady, onStart, promptAutoPageTour, user?.preferences, afterAnimationMs]);
}

/**
 * Auto-starts a tour built from dynamic steps. The launch callback must return a cancel function.
 */
export function useAutoTourSteps(
  isReady: boolean,
  launch: () => (() => void) | void,
  enabled = true,
) {
  useEffect(() => {
    if (!isReady || !enabled) return undefined;
    const cancel = launch();
    return typeof cancel === 'function' ? cancel : undefined;
  }, [enabled, isReady, launch]);
}
